import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, TagIcon, ChevronRightIcon, SendIcon, CheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, TextArea } from '../components/ui/Input';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';

// ─────────────────────────────────────────────────────────────
// FORM EMAIL — same as Contact page
// ─────────────────────────────────────────────────────────────
const FORM_EMAIL = 'helgi@helgig.com';

// ─────────────────────────────────────────────────────────────
// EVENT DATA
// Update this list to add/remove/modify scheduled events.
//
// priceIS / priceEN: shown on the card, e.g. "49.900 kr." or "Contact for pricing"
// status: 'open' | 'few-left' | 'full' | 'coming-soon'
// ─────────────────────────────────────────────────────────────
interface ScheduledEvent {
  id: string;
  titleIS: string;
  titleEN: string;
  dateIS: string;
  dateEN: string;
  timeIS: string;
  timeEN: string;
  locationIS: string;
  locationEN: string;
  formatIS: string;
  formatEN: string;
  priceIS: string;
  priceEN: string;
  spotsTotal: number | null;
  spotsLeft: number | null;
  status: 'open' | 'few-left' | 'full' | 'coming-soon';
  internalPath: string;
  // Skráningarsíða. Hnappurinn "Skrá mig" birtist þegar status er 'open' eða 'few-left'.
  signupPath?: string;
  tagIS: string;
  tagEN: string;
  accentColor: string;
  accentText: string;
  accentLight: string;
}
const events: ScheduledEvent[] = [{
  id: 'conflict-2026-27',
  titleIS: 'Betri samskipti og heilbrigðari ágreiningar',
  titleEN: 'Better conversations, healthier conflict',
  dateIS: '25. nóv · 9. des · 20. jan',
  dateEN: '25 Nov · 9 Dec · 20 Jan',
  timeIS: 'Hálfur dagur 9-12:30 · heill dagur 9-16',
  timeEN: 'Half day 9-12:30 · full day 9-16',
  locationIS: 'Reykjavík',
  locationEN: 'Reykjavík',
  formatIS: 'Hálfur dagur eða heill dagur',
  formatEN: 'Half day or full day (taught in Icelandic)',
  priceIS: 'Frá 34.900 kr.',
  priceEN: 'From 34,900 ISK',
  spotsTotal: 14,
  spotsLeft: null,
  status: 'open',
  internalPath: '/services/courses/conflict-coaching',
  signupPath: '/events/betri-samskipti',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-teal',
  accentText: 'text-teal',
  accentLight: 'bg-teal/10'
}, {
  id: 'ftm-autumn-2026',
  titleIS: 'Stjórnandi í Fyrsta Sinn',
  titleEN: 'First-Time Manager',
  dateIS: 'Dagsetning í vinnslu',
  dateEN: 'Date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '6 vikur · Ein lota á viku',
  formatEN: '6 weeks · One session per week',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/first-time-manager',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-coral',
  accentText: 'text-coral',
  accentLight: 'bg-coral/10'
}, {
  id: 'meetings-autumn-2026',
  titleIS: 'Betri Fjarfundir',
  titleEN: 'Better Online Meetings',
  dateIS: 'Dagsetning í vinnslu',
  dateEN: 'Date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '2 x 2 klst',
  formatEN: '2 x 2 hrs',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/better-online-meetings',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-blue-600',
  accentText: 'text-blue-600',
  accentLight: 'bg-blue-50'
}];

// ─────────────────────────────────────────────────────────────
// Registration modal / inline form
// ─────────────────────────────────────────────────────────────
interface RegFormProps {
  event: ScheduledEvent;
  isIS: boolean;
  onClose: () => void;
}
function RegistrationForm({
  event,
  isIS,
  onClose
}: RegFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const title = isIS ? event.titleIS : event.titleEN;
  const date = isIS ? event.dateIS : event.dateEN;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(p => ({
      ...p,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message || '—',
          _subject: `Course registration interest: ${title}`,
          course: title,
          date
        })
      });
      if (res.ok) {
        setIsSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-1193" data-mp="6dy85nzowuzh">
      <Animate data-id="element-1194" data-mp="2cmdv55vflky">
        <Card className="w-full max-w-md p-8 relative shadow-2xl" data-id="element-1195" data-mp="g9rpdkoctcnl">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-1196" data-mp="g7x5m9otjql3">
            <XIcon size={20} data-id="element-1197" data-mp="354r7poposkch" />
          </button>

          {isSubmitted ? <div className="text-center py-6" data-id="element-1198" data-mp="395b5mui33rzo">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1199" data-mp="3e3l2randd2nx">
                <CheckIcon size={28} strokeWidth={2.5} data-id="element-1200" data-mp="3iukum1hmsujk" />
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-1201" data-mp="3nto2dtozxev8">
                {isIS ? 'Skráning móttekin!' : 'Got it!'}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-1202" data-mp="3sr6695o5p3wd">
                {isIS ? 'Ég kem til baka til þín þegar dagsetning er staðfest.' : "I'll be in touch as soon as the date is confirmed."}
              </p>
              <Button variant="outline" onClick={onClose} size="sm" data-id="element-1203" data-mp="22gkzd3tqdt2n">
                {isIS ? 'Loka' : 'Close'}
              </Button>
            </div> : <>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${event.accentLight} ${event.accentText}`} data-id="element-1204" data-mp="1zvn2xu6tgl01">
                {isIS ? event.tagIS : event.tagEN}
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-1" data-id="element-1205" data-mp="1q5k7zog4wwz2">{title}</h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-1206" data-mp="1q3h7ubl9pngm">
                {isIS ? 'Láttu mig vita og ég kem til baka þegar dagsetning er staðfest.' : 'Leave your details and I will contact you when the date is confirmed.'}
              </p>

              {hasError && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm mb-4 border border-red-100" data-id="element-1207" data-mp="2dvxrgqknlni1">
                  {isIS ? 'Eitthvað fór úrskeiðis. Sendu beint á ' : 'Something went wrong. Email directly: '}
                  <a href={`mailto:${FORM_EMAIL}`} className="underline font-bold" data-id="element-1208" data-mp="24wstpmgx1y6x">{FORM_EMAIL}</a>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1209" data-mp="tf1hidac3a2g">
                <Input label={isIS ? 'Nafn' : 'Name'} name="name" placeholder={isIS ? 'Jón Jónsson' : 'Jane Smith'} value={formData.name} onChange={handleChange} required data-id="element-1210" data-mp="thxhs89nmau2" />
                <Input label={isIS ? 'Netfang' : 'Email'} name="email" type="email" placeholder={isIS ? 'jon@example.com' : 'jane@example.com'} value={formData.email} onChange={handleChange} required data-id="element-1211" data-mp="2jlzd5jprw7e6" />
                <TextArea label={isIS ? 'Spurningar eða athugasemdir (valfrjálst)' : 'Questions or comments (optional)'} name="message" rows={3} placeholder={isIS ? 'Hér má setja spurningar eða sérstakar óskir...' : 'Any questions or special requirements...'} value={formData.message} onChange={handleChange} data-id="element-1212" data-mp="32jab90zo4us1" />
                <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<SendIcon size={16} data-id="element-1214" data-mp="mwlsrtpx0rmv" />} data-id="element-1213" data-mp="d5hdeswbv4ri">
                  {isIS ? 'Láta vita' : 'Notify me'}
                </Button>
              </form>
            </>}
        </Card>
      </Animate>
    </div>;
}

// ─────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────
function StatusBadge({
  event,
  isIS
}: {
  event: ScheduledEvent;
  isIS: boolean;
}) {
  const map = {
    open: {
      label: isIS ? 'Skráning opin' : 'Open',
      classes: 'bg-green-100 text-green-700'
    },
    'few-left': {
      label: isIS ? 'Fá pláss eftir' : 'Few spots left',
      classes: 'bg-amber-100 text-amber-700'
    },
    full: {
      label: isIS ? 'Fullskráð' : 'Full',
      classes: 'bg-red-100 text-red-700'
    },
    'coming-soon': {
      label: isIS ? 'Í vinnslu' : 'Coming soon',
      classes: 'bg-gray-100 text-gray-500'
    }
  };
  const s = map[event.status];
  return <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${s.classes}`} data-id="element-1215" data-mp="18nsplh7kyg6m">
      {s.label}
    </span>;
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export function Events() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const [activeRegistration, setActiveRegistration] = useState<ScheduledEvent | null>(null);
  return <PageTransition data-id="element-1216" data-mp="ky7vc25z7wi1">
      {/* Registration modal */}
      {activeRegistration && <RegistrationForm event={activeRegistration} isIS={isIS} onClose={() => setActiveRegistration(null)} data-id="element-1217" data-mp="fzesd3obfnw4" />}

      <div className="pt-12 pb-24" data-id="element-1218" data-mp="2f93c3rrbehvw">
        {/* Header */}
        <section className="text-center max-w-4xl mx-auto px-4 mb-16 relative" data-id="element-1219" data-mp="2ado075cvaw24">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1220" data-mp="2isbznfae733v" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-24 h-24 -z-10" data-id="element-1221" data-mp="2mtnqfo505udn" />
          <Animate data-id="element-1222" data-mp="2rm8hwj0frve0">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1223" data-mp="2rnry69m3e6fs">
              {isIS ? 'Námskeið og vinnustofur' : 'Courses and workshops'}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-1224" data-mp="1zwvotiz8qjx9">
              {isIS ? 'Næstu viðburðir' : 'Upcoming events'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1225" data-mp="28uxs656g5onp">
              {isIS ? 'Skráning er opin á námskeiðið Betri samskipti og heilbrigðari ágreiningar. Önnur námskeið eru í undirbúningi: skráðu þig á biðlista og ég læt þig vita þegar dagsetningar liggja fyrir.' : 'Registration is open for the conflict course (taught in Icelandic). Other courses are being scheduled: join the waiting list and I will let you know when dates are set.'}
            </p>
          </Animate>
        </section>

        {/* Event cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8" data-id="element-1226" data-mp="39jhjjgdihxyn">
          {events.map((event, i) => <Animate key={event.id} delay={i * 0.08} data-id="element-1227" data-mp="34jv89ch8ursh" data-mpi={i}>
              <Card className="p-0 overflow-hidden border-2 border-gray-100 transition-colors" data-id="element-1228" data-mp="34l6d7fmfe5l6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1229" data-mp="2gv3kqyelmxm1">
                  {/* Accent panel */}
                  <div className={`lg:col-span-3 ${event.accentLight} p-8 flex flex-col justify-center items-center text-center gap-3`} data-id="element-1230" data-mp="2btwcky8jnweu">
                    <div className={`w-14 h-14 rounded-full ${event.accentColor} text-white flex items-center justify-center shadow-md`} data-id="element-1231" data-mp="2ltn8n4cr6jh1">
                      <CalendarIcon size={24} data-id="element-1232" data-mp="1y2htkremzv7s" />
                    </div>
                    <div data-id="element-1233" data-mp="1t46lal3zpsro">
                      <p className={`font-bold text-base ${event.accentText} leading-snug`} data-id="element-1234" data-mp="1t4ziovh275x5">
                        {isIS ? event.dateIS : event.dateEN}
                      </p>
                      {(isIS ? event.timeIS : event.timeEN) && <p className="text-xs text-gray-500 mt-1.5 flex items-center justify-center gap-1" data-id="element-1235" data-mp="3uuxa8z0wq9oq">
                          <ClockIcon size={11} data-id="element-1236" data-mp="3ncdulgfieug" />
                          {isIS ? event.timeIS : event.timeEN}
                        </p>}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.accentLight} ${event.accentText}`} data-id="element-1237" data-mp="wcjebpzg1eic">
                      {isIS ? event.tagIS : event.tagEN}
                    </span>
                  </div>

                  {/* Content panel */}
                  <div className="lg:col-span-9 p-8 bg-white flex flex-col justify-between gap-5" data-id="element-1238" data-mp="mggamfoy17ta">
                    <div data-id="element-1239" data-mp="335lrjmx91lys">
                      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap" data-id="element-1240" data-mp="374kqmqwauoib">
                        <h2 className="text-2xl font-bold text-charcoal" data-id="element-1241" data-mp="3nbpdi8a4kqzz">
                          {isIS ? event.titleIS : event.titleEN}
                        </h2>
                        <StatusBadge event={event} isIS={isIS} data-id="element-1242" data-mp="3ij5zxbbftvv6" />
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500" data-id="element-1243" data-mp="3dmfmkvr0pedg">
                        <span className="flex items-center gap-1.5" data-id="element-1244" data-mp="38ow6rreo8tje">
                          <MapPinIcon size={13} className="flex-shrink-0" data-id="element-1245" data-mp="a4e7c1j811d9" />
                          {isIS ? event.locationIS : event.locationEN}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-1246" data-mp="6q9h2qbjdqv">
                          <TagIcon size={13} className="flex-shrink-0" data-id="element-1247" data-mp="942z75cohwb" />
                          {isIS ? event.formatIS : event.formatEN}
                        </span>
                        {event.spotsTotal && <span className="flex items-center gap-1.5" data-id="element-1248" data-mp="2gw5716xs4mpe">
                            <UsersIcon size={13} className="flex-shrink-0" data-id="element-1249" data-mp="2gxztwqsm2ow4" />
                            {isIS ? `Hámark ${event.spotsTotal}` : `Max ${event.spotsTotal}`}
                          </span>}
                        {event.status !== 'coming-soon' && <span className="font-bold text-charcoal" data-id="element-1250" data-mp="15d7wk4sl3h7d">{isIS ? event.priceIS : event.priceEN}</span>}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-5 border-t border-gray-100" data-id="element-1251" data-mp="1f42mpk8yb0y3">
                      {event.signupPath && (event.status === 'open' || event.status === 'few-left') ? <Link to={event.signupPath} data-id="element-1252" data-mp="1t44yqpukv05f">
                          <Button rightIcon={<ChevronRightIcon size={15} data-id="element-1254" data-mp="1t20zil7mipbh" />} data-id="element-1253" data-mp="1t3lhlbcr4fsg">
                            {isIS ? 'Skrá mig' : 'Register'}
                          </Button>
                        </Link> : <button onClick={() => setActiveRegistration(event)} className="inline-flex" data-id="element-1255" data-mp="3uxbc8zqpy2c4">
                        <Button variant="outline" rightIcon={<ChevronRightIcon size={15} data-id="element-1257" data-mp="1b1ofze8fnxor" />} data-id="element-1256" data-mp="x3hs8qx77e2t">
                          {isIS ? 'Láta vita þegar skráning opnar' : 'Notify me when registration opens'}
                        </Button>
                      </button>}

                      <Link to={event.internalPath} data-id="element-1258" data-mp="1kykqtqlt0vwh">
                        <Button variant="outline" size="sm" data-id="element-1259" data-mp="1g112fxve72qt">
                          {isIS ? 'Um námskeiðið' : 'About the course'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            </Animate>)}
        </section>

        {/* Payment note */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-1260" data-mp="23qmywdkdr3v4">
          <Animate data-id="element-1261" data-mp="1ysli5zaffgmt">
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5" data-id="element-1262" data-mp="1yt3o5nmw597x">
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-1263" data-mp="2idu0hsnz6phc">i</div>
              <p className="text-sm text-gray-600" data-id="element-1264" data-mp="28fo39od467it">
                {isIS ? 'Á opnum námskeiðum greiðir þú með korti eða færð reikning við skráningu. Flest stéttarfélög styrkja námskeið af þessu tagi og vinnuveitendur geta sótt um styrk í gegnum attin.is. Spurningar? Hafðu samband á ' : 'For open courses you pay by card or receive an invoice when you register. Most Icelandic unions subsidise courses like these, and employers can apply for a grant via attin.is. Questions? Contact me at '}
                <a href={`mailto:${FORM_EMAIL}`} className="text-teal font-medium hover:underline" data-id="element-1265" data-mp="1unzqdiv1jf0s">{FORM_EMAIL}</a>
              </p>
            </div>
          </Animate>
        </section>

        {/* Custom / in-house CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-16" data-id="element-1266" data-mp="1kreov7i8cm1p">
          <Animate data-id="element-1267" data-mp="213rqgn1xdhja">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-1268" data-mp="2141lwq5qogwd">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1269" data-mp="1w6hvkrvrodo2">
                {isIS ? 'Ertu að leita að sérsniðinni þjálfun?' : 'Looking for something tailored?'}
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto" data-id="element-1270" data-mp="1m6ipd1mtkzed">
                {isIS ? 'Ég hanna og held námskeið og vinnustofur innanhúss hjá fyrirtækjum - aðlagað að ykkar samhengi og þörfum.' : 'I design and run in-house courses and workshops for organisations - adapted to your context and needs.'}
              </p>
              <Link to="/contact" data-id="element-1271" data-mp="13h2htucpw6og">
                <Button variant="secondary" rightIcon={<ChevronRightIcon size={16} data-id="element-1273" data-mp="13ey8gww1ufgs" />} data-id="element-1272" data-mp="13i3vm6l4b79v">
                  {isIS ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}